import { Component } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { VALID_PASSWORD } from '../../../../../core/constants/general.constant';
import { SuccessPopupComponent } from '../../../../../core/libraries/popup/components/success-popup/success-popup.component';
import { IResetPasswordData } from '../../../../../core/models/auth.model';
import { PasswordService } from '../../../../../core/services/api/password/password.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-reset-password',
    standalone: false,
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
    protected token = this.activatedRoute.snapshot.queryParamMap.get('token') || '';
    protected resetPasswordForm = this.fb.group(
        {
            password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(VALID_PASSWORD)]],
            confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.pattern(VALID_PASSWORD)]],
        },
        { validators: this.passwordMatchValidator },
    );
    protected showPassword = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private fb: NonNullableFormBuilder,
        private loaderService: LoaderService,
        private passwordService: PasswordService,
        private router: Router,
    ) {}

    get password(): string {
        return this.resetPasswordForm.get('password')?.value || '';
    }

    protected hasMinLength(): boolean {
        return this.password.length >= 8;
    }

    protected hasNumber(): boolean {
        return /\d/.test(this.password);
    }

    protected hasLetter(): boolean {
        return /[a-zA-Z]/.test(this.password);
    }

    protected submit(): void {
        if (this.resetPasswordForm.invalid || !this.token) {
            return;
        }

        const body: IResetPasswordData = {
            token: this.token,
            password: this.resetPasswordForm.controls.password.value,
        };

        this.loaderService.show();
        this.passwordService
            .resetPassword(body)
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: () => {
                    const popupConfig = SuccessPopupComponent.getDialogConfig();
                    popupConfig.data = {
                        title: `Welcome back!`,
                        message: `Your password has been refreshed and you are ready to log in.`,
                        confirmButtonLabel: 'Log In',
                    };
                    this.dialog
                        .open(SuccessPopupComponent, popupConfig)
                        .afterClosed()
                        .subscribe(() => {
                            this.router.navigate(['/landing']);
                        });
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                },
            });
    }

    protected togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }

    private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.get('password')?.value;
        const confirmPassword = control.get('confirmPassword')?.value;
        return password && confirmPassword && password !== confirmPassword ? { passwordMismatch: true } : null;
    }
}
